import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";

/* ---------------- WordsPullUp ---------------- */
const WordsPullUp = ({ text, className = "", showAsterisk = false, style }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const words = text.split(" ");

  return (
    <div ref={ref} className={`inline-flex flex-wrap ${className}`} style={style}>
      {words.map((word, i) => {
        const isLast = i === words.length - 1;
        return (
          <motion.span
            key={i}
            initial={{ y: 20, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block relative"
            style={{ marginRight: isLast ? 0 : "0.25em" }}
          >
            {word}
            {showAsterisk && isLast && (
              <span className="absolute top-[0.65em] -right-[0.3em] text-[0.31em]">*</span>
            )}
          </motion.span>
        );
      })}
    </div>
  );
};

/* ---------------- Hero slide data ---------------- */
const HERO_SLIDES = [
  {
    id: "grit",
    eyebrow: null,
    heading: "Nesolearn",
    body: "Every course you finish is ground you didn't have before, carved out alone, on your own schedule, against everything trying to pull your focus away. Nesolearn is built for the ones who keep showing up anyway.",
    cta: { label: "Sign Up for Nesolearn", to: "/authentication-portal" },
    media: {
      type: "video",
      src: "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260405_170732_8a9ccda6-5cff-4628-b164-059c500a2b41.mp4",
    },
  },
    {
    id: "intelligence",
    eyebrow: "MOU signed with Africa Web3 Institute",
    heading: "A Learning Intelligence Platform",
    body: "Every course, badge, and skill becomes a verifiable, portable credential, secured on-chain so nothing can be faked and nothing can be lost.",
    cta: { label: "See How It Works", anchor: "#identity-spotlight" },
    media: { type: "visual", variant: "default" },
  },
  {
    id: "institutions",
    eyebrow: "For Institutions",
    heading: "Issue Credentials Institutions Can Trust",
    body: "Deliver, track, and certify your programs on infrastructure built for verification, reduce certificate fraud and give every graduate a credential that holds up anywhere.",
    cta: { label: "Register Your Institution", to: "/institutions-signup" },
    media: { type: "visual", variant: "institutions" },
  },
  {
    id: "identity",
    eyebrow: "For Learners",
    heading: "Explore Your Digital Identity",
    body: "Claim the identity that carries your credentials with you, every course, badge, and certification, verifiable and yours for good.",
    cta: { label: "Claim Your Identity", to: "/authentication-portal" },
    media: { type: "visual", variant: "identity" },
  },
];

const VISUAL_THEMES = {
  default:
    "radial-gradient(circle at 20% 30%, rgba(120,255,214,0.18), transparent 45%), radial-gradient(circle at 80% 70%, rgba(120,170,255,0.16), transparent 45%), linear-gradient(180deg, #05070a 0%, #0b0f14 100%)",
  institutions:
    "radial-gradient(circle at 25% 25%, rgba(255,196,120,0.16), transparent 45%), radial-gradient(circle at 75% 75%, rgba(255,140,90,0.14), transparent 45%), linear-gradient(180deg, #07060a 0%, #10090b 100%)",
  identity:
    "radial-gradient(circle at 30% 20%, rgba(200,140,255,0.18), transparent 45%), radial-gradient(circle at 70% 80%, rgba(140,120,255,0.16), transparent 45%), linear-gradient(180deg, #08060f 0%, #0c0a14 100%)",
};

const AUTOPLAY_MS = 7000;

/* ---------------- Hero ---------------- */
const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const slide = HERO_SLIDES[activeIndex];

  useEffect(() => {
    if (isPaused) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [isPaused]);

  const goTo = (index) => setActiveIndex(index);

  const handleCtaClick = (e, cta) => {
    if (cta.anchor) {
      e.preventDefault();
      document.querySelector(cta.anchor)?.scrollIntoView({ behavior: "smooth" });
    }
  };
    return (
    <section
      className="h-screen w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="relative h-full w-full overflow-hidden">

        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full"
          >
            {/* Background media */}
            {slide.media.type === "video" ? (
              <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
                src={slide.media.src}
              />
            ) : (
                            <div className="absolute inset-0 h-full w-full bg-black">
                {/* Premium fallback visual — swap for a real video/image via slide.media.src when ready */}
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    backgroundImage: VISUAL_THEMES[slide.media.variant || "default"],
                  }}
                />
                <div
                  className="absolute inset-0 opacity-[0.12]"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                  }}
                />
              </div>
            )}

            {/* Noise overlay */}
            <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.7] mix-blend-overlay" />

            {/* Gradient overlay */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/70" />

            {/* Hero content */}
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-2 sm:px-6 md:px-10">
              <div className="grid grid-cols-12 items-end gap-4">

                <div className="col-span-12 lg:col-span-8">
                  {slide.eyebrow && (
                    <motion.span
                      initial={{ y: 12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="mb-3 inline-block rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[0.65rem] font-medium uppercase tracking-wider text-white/85 backdrop-blur-sm sm:text-xs"
                    >
                      {slide.eyebrow}
                    </motion.span>
                  )}
                  <h1 className="font-medium leading-[0.85] tracking-[-0.04em] text-white text-[12vw] sm:text-[11vw] md:text-[9vw] lg:text-[7.5vw] xl:text-[6.5vw] 2xl:text-[7vw]">
                    <WordsPullUp text={slide.heading} hideAsterisk />
                  </h1>
                </div>

                <div className="col-span-12 flex flex-col gap-5 pb-6 lg:col-span-4 lg:pb-10">

                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="text-xs text-white/85 sm:text-sm md:text-base"
                    style={{ lineHeight: 1.2 }}
                  >
                    {slide.body}
                  </motion.p>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="self-start"
                  >
                    <Link
                      to={slide.cta.to || "#"}
                      onClick={(e) => handleCtaClick(e, slide.cta)}
                      className="group inline-flex items-center gap-2 rounded-full bg-white py-1 pl-5 pr-1 text-sm font-medium text-black transition-all hover:gap-3 sm:text-base"
                    >
                      {slide.cta.label}
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black transition-transform group-hover:scale-110 sm:h-10 sm:w-10">
                        <ArrowRight className="h-4 w-4 text-white" />
                      </span>
                    </Link>
                  </motion.div>

                </div>
              </div>

              {/* Slide indicators */}
              <div className="mt-6 flex items-center gap-2 pb-2">
                {HERO_SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => goTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-[3px] rounded-full transition-all duration-500 ${
                      i === activeIndex ? "w-8 bg-white" : "w-4 bg-white/35 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HeroSection;